<template>
  <popover
    :target="target"
    triggers="hover"
    custom-class="parent-goal-popover"
    placement="top"
    @shown="onShown"
    @hide="onHide"
  >
    <div v-if="loadingState === LoadingState.LOADING">
      <spinner />
    </div>
    <div v-else-if="loadingState === LoadingState.ERRORED">
      {{ error }}
    </div>
    <div class="popover-container" v-else>
      <div class="node node-content">
        <div class="d-flex flex-grow-1 bg-white mb-1" v-for="pGoal in parentGoals" :key="pGoal.id">
          <i class="goal-icon-greyed flex-shrink-0"></i>
          <div class="d-flex flex-column flex-grow-1 ml-2 text-medium">
            <router-link :to="getGoalPageLocation(pGoal)" class="text-primary">
              <span v-no-capture>{{ pGoal.title }}</span>
            </router-link>
            <goal-meta :goal="pGoal" />
          </div>
        </div>
        <div class="child-node">
          <div class="d-flex node">
            <i class="goal-icon flex-shrink-0"></i>
            <div class="ml-2">
              <router-link
                :to="getGoalPageLocation(goal)"
                class="text-medium text-primary"
                v-no-capture
              >
                {{ goal.title }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <link-button
        class="text-medium underline manage-alignment-btn"
        @click="parentGoalAlignmentShown = true"
        >Manage alignment</link-button
      >
      <parent-goals-alignment
        :org-id="goal.organizationId"
        :sub-org-id="subOrgId"
        :goal="goal"
        :save-on-submit="true"
        v-model="parentGoalAlignmentShown"
        v-if="parentGoalAlignmentShown"
      />
    </div>
  </popover>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton'
import GoalMeta from '@/components/goal/GoalMeta.vue'
import ParentGoalsAlignment from '@/components/goal/ParentGoalsAlignment'
import Popover from '@/components/Popover'
import Spinner from '@/components/Spinner'
import { LoadingState } from '@/constants/common'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import TeamMixin from '@/mixins/TeamMixin'
import { getGoalPageRouteConfig } from '@/utils/navigationConfigs'
import * as R from 'ramda'

/**
 * This shows parent goals of prop - goalId
 * This can be typically used in places where we want to show parent goals for a goal
 */
export default {
  name: 'ParentGoalsPopover',
  props: ['goal', 'target'],
  mixins: [TeamMixin, EventBusMixin],
  components: {
    GoalMeta,
    Popover,
    Spinner,
    ParentGoalsAlignment,
    LinkButton
  },
  data() {
    return {
      error: null,
      parentGoals: null,
      loadingState: LoadingState.LOADING,
      LoadingState,
      parentGoalAlignmentShown: false,
      subOrgId: this.getClosestOrgLikeTeamId(this.goal.teamId)
    }
  },
  methods: {
    sortGoalsByTeam(goals) {
      if (goals.length === 0) return goals
      const orgId = goals[0].organizationId
      const rootTeam = this.$store.getters.rootTeamByOrgId(orgId)
      const [orgGoals, teamGoals] = R.partition(goal => goal.teamId === rootTeam.id, goals)
      return [...orgGoals, ...R.sort(x => x.teamId, teamGoals)]
    },
    getGoalPageLocation(goal) {
      return getGoalPageRouteConfig({
        orgId: goal.organizationId,
        goalId: goal.id
      })
    },
    async refreshParentGoals() {
      const goals = await this.$store.dispatch('getGoals', {
        orgId: this.goal.organizationId,
        options: {
          childId: this.goal.id
        }
      })
      this.parentGoals = this.sortGoalsByTeam(goals)
      this.parentGoals.forEach(pGoal => {
        if (pGoal.ownerId && !this.$store.getters.userById(pGoal.ownerId)) {
          this.$store.dispatch('getUser', { id: pGoal.ownerId })
        }
      })
    },
    async onShown() {
      try {
        if (!this.parentGoals) {
          this.loadingState = LoadingState.LOADING
          await this.refreshParentGoals()
          this.loadingState = LoadingState.LOADED
        } else if (
          this.goal.parentGoalIds.length !== this.parentGoals.length ||
          this.goal.parentGoalIds.some(id => !this.parentGoals.some(goal => goal.id === id))
        ) {
          // if parent goal is not present in the list
          this.loadingState = LoadingState.LOADING
          await this.refreshParentGoals()
          this.loadingState = LoadingState.LOADED
        }
      } catch (e) {
        this.loadingState = LoadingState.ERRORED
        this.error = 'Parent goal information could not be fetched'
      }
    },
    onHide(event) {
      if (this.parentGoalAlignmentShown) {
        event.preventDefault()
      }
    }
  },
  mounted() {
    this.listenToEventBus(
      events.PARENT_GOAL_ALIGNMENT_CHANGED,
      () => this.parentGoalAlignmentShown && this.refreshParentGoals()
    )
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

@include treeNodeConnector(9px, 8px, 7px, -10px, 11px, -23px);

.popover-container {
  min-width: 220px;
}

.goal-icon,
.goal-icon-greyed {
  margin-top: 3px;
}

.child-node {
  padding-top: 8px;
  padding-left: 25px;
}

.manage-alignment-btn {
  padding: 0 0.5rem;
  margin-top: 1.25rem;
}
</style>
<style lang="scss">
.parent-goal-popover {
  max-width: 370px;
}
</style>
